import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ImagePlus, Loader2, Star } from "lucide-react";
import { RestaurantHoursEditor } from "./RestaurantHoursEditor";
import { RestaurantProfileEditor } from "./RestaurantProfileEditor";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EMPTY = { name: "", description: "", price: "", image_url: "", menu_category_id: "" };

const DishForm = ({ user, dish, defaultCat, categories, onClose, onSaved }) => {
  const [f, setF] = useState(dish ? { ...dish, price: String(dish.price) } : { ...EMPTY, menu_category_id: defaultCat || categories[0]?.id || "" });
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("vendor_id", user.id);
      const r = await axios.post(`${API}/products/upload-image`, fd);
      setF((x) => ({ ...x, image_url: `${BACKEND_URL}${r.data.image_url}` }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi de la photo");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!f.name || !f.price || !f.image_url || !f.menu_category_id) return toast.error("Photo, nom, prix et catégorie sont obligatoires");
    const body = { name: f.name, description: f.description || "", price: parseInt(f.price, 10), image_url: f.image_url, menu_category_id: f.menu_category_id };
    try {
      if (dish) await axios.put(`${API}/restaurant/dishes/${dish.id}`, body);
      else await axios.post(`${API}/restaurant/dishes`, body);
      toast.success(dish ? "Plat modifié" : "Plat ajouté");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F172A] border-white/10 text-white" data-testid="dish-form-dialog">
        <DialogHeader><DialogTitle>{dish ? "Modifier le plat" : "Nouveau plat"}</DialogTitle></DialogHeader>
        <label className="block h-36 rounded-2xl border border-dashed border-white/20 overflow-hidden cursor-pointer flex items-center justify-center" data-testid="dish-photo-label">
          {uploading ? <Loader2 className="animate-spin" /> : f.image_url ? <img src={f.image_url} alt="" className="w-full h-full object-cover" /> : <span className="text-xs text-slate-400 flex items-center gap-2"><ImagePlus size={16} /> Photo du plat</span>}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="dish-photo-input" />
        </label>
        <Input placeholder="Nom du plat" value={f.name} onChange={set("name")} className="bg-[#1E293B] border-white/10" data-testid="dish-name-input" />
        <Input placeholder="Description" value={f.description} onChange={set("description")} className="bg-[#1E293B] border-white/10" data-testid="dish-description-input" />
        <Input placeholder="Prix (FCFA)" type="number" min="1" value={f.price} onChange={set("price")} className="bg-[#1E293B] border-white/10" data-testid="dish-price-input" />
        <select value={f.menu_category_id} onChange={set("menu_category_id")} className="w-full h-10 rounded-md bg-[#1E293B] border border-white/10 px-3 text-sm" data-testid="dish-category-select">
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button type="button" onClick={save} disabled={uploading} className="w-full rounded-full bg-[#D4AF37] text-[#0F172A] py-3 text-xs font-black uppercase tracking-widest" data-testid="dish-save-btn">Enregistrer</button>
      </DialogContent>
    </Dialog>
  );
};

const FeatureDialog = ({ dish, isFeatured, onClose, onDone }) => {
  const go = async (body, msg) => {
    try {
      await axios.put(`${API}/restaurant/settings/featured`, body);
      toast.success(msg);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };
  const btn = "w-full rounded-full py-3 text-xs font-black uppercase tracking-widest";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F172A] border-white/10 text-white" data-testid="featured-dialog">
        <DialogHeader><DialogTitle>Plat du jour : {dish.name}</DialogTitle></DialogHeader>
        <button type="button" className={`${btn} bg-[#D4AF37] text-[#0F172A]`} onClick={() => go({ dish_id: dish.id, mode: "today" }, "Plat du jour jusqu'à minuit")} data-testid="featured-today-btn">Aujourd'hui seulement</button>
        <button type="button" className={`${btn} bg-white/10`} onClick={() => go({ dish_id: dish.id, mode: "always" }, "Plat mis en avant sans limite")} data-testid="featured-always-btn">Sans limite de durée</button>
        {isFeatured && <button type="button" className={`${btn} text-rose-300`} onClick={() => go({ dish_id: null }, "Plat du jour retiré")} data-testid="featured-remove-btn">Retirer le plat du jour</button>}
      </DialogContent>
    </Dialog>
  );
};

const DishRow = ({ d, featured, onFeature, onEdit, onToggle, onDelete }) => (
  <div className="flex items-center gap-3 bg-[#0F172A] rounded-xl p-2" data-testid={`manage-dish-${d.id}`}>
    <img src={d.image_url} alt="" className={`w-12 h-12 rounded-lg object-cover ${d.is_available ? "" : "opacity-40 grayscale"}`} />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-bold truncate">{d.name}</p>
      <p className="text-[11px] text-[#D4AF37]">{Number(d.price).toLocaleString("fr-FR")} FCFA · {d.is_available ? "Disponible" : "Indisponible"}</p>
    </div>
    <button type="button" onClick={() => onFeature(d)} title="Plat du jour" className={`p-2 ${featured ? "text-[#D4AF37]" : "text-slate-500"}`} data-testid={`dish-feature-${d.id}`}><Star size={14} fill={featured ? "currentColor" : "none"} /></button>
    <Switch className="shrink-0" checked={d.is_available} onCheckedChange={(v) => onToggle(d, v)} data-testid={`dish-available-${d.id}`} />
    <button type="button" onClick={() => onEdit(d)} className="p-2 text-slate-300" data-testid={`dish-edit-${d.id}`}><Pencil size={14} /></button>
    <button type="button" onClick={() => onDelete(d)} className="p-2 text-rose-300" data-testid={`dish-delete-${d.id}`}><Trash2 size={14} /></button>
  </div>
);

// Gestion du menu du restaurant : catégories + plats (photo, nom, description, prix, disponibilité)
export const RestaurantMenuManager = ({ user }) => {
  const [cats, setCats] = useState(null);
  const [newCat, setNewCat] = useState("");
  const [form, setForm] = useState(null);
  const [featuredId, setFeaturedId] = useState(null);
  const [featureDish, setFeatureDish] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/restaurant/menu`).then((r) => setCats(r.data.categories)).catch(() => setCats([]));
    axios.get(`${API}/restaurant/settings`).then((r) => setFeaturedId(r.data.featured_active)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    try {
      await fn();
      if (ok) toast.success(ok);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };
  const addCat = () => newCat.trim() && act(() => axios.post(`${API}/restaurant/categories`, { name: newCat.trim(), display_order: cats.length }), "Catégorie ajoutée").then(() => setNewCat(""));
  const renameCat = (c) => {
    const name = window.prompt("Nouveau nom de la catégorie", c.name);
    if (name?.trim()) act(() => axios.put(`${API}/restaurant/categories/${c.id}`, { name: name.trim(), display_order: c.display_order }), "Catégorie renommée");
  };
  const deleteCat = (c) => window.confirm(`Supprimer « ${c.name} » et ses ${c.dishes.length} plat(s) ?`) && act(() => axios.delete(`${API}/restaurant/categories/${c.id}`), "Catégorie supprimée");
  const toggle = (d, v) => act(() => axios.put(`${API}/restaurant/dishes/${d.id}`, { is_available: v }), v ? "Plat disponible" : "Plat masqué aux clients");
  const delDish = (d) => window.confirm(`Supprimer « ${d.name} » ?`) && act(() => axios.delete(`${API}/restaurant/dishes/${d.id}`), "Plat supprimé");

  if (!cats) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#D4AF37]" /></div>;
  return (
    <section className="space-y-5" data-testid="restaurant-menu-manager">
      <RestaurantProfileEditor user={user} />
      <RestaurantHoursEditor />
      <div className="flex gap-2">
        <Input placeholder="Nouvelle catégorie (Entrées, Plats, Desserts, Boissons…)" value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCat()} className="bg-[#1E293B] border-white/10" data-testid="new-category-input" />
        <button type="button" onClick={addCat} className="rounded-full bg-[#D4AF37] text-[#0F172A] px-4 text-xs font-black" data-testid="add-category-btn"><Plus size={16} /></button>
      </div>
      {cats.length === 0 && <p className="text-xs text-slate-400" data-testid="menu-empty">Créez une première catégorie pour ajouter vos plats.</p>}
      {cats.map((c) => (
        <div key={c.id} className="bg-[#1E293B] border border-white/5 rounded-2xl p-4 space-y-3" data-testid={`manage-category-${c.id}`}>
          <div className="flex items-center gap-2">
            <h3 className="flex-1 font-black uppercase tracking-widest text-xs text-[#D4AF37]">{c.name}</h3>
            <button type="button" onClick={() => renameCat(c)} className="p-1.5 text-slate-300" data-testid={`category-rename-${c.id}`}><Pencil size={14} /></button>
            <button type="button" onClick={() => deleteCat(c)} className="p-1.5 text-rose-300" data-testid={`category-delete-${c.id}`}><Trash2 size={14} /></button>
          </div>
          {c.dishes.map((d) => <DishRow key={d.id} d={d} featured={featuredId === d.id} onFeature={setFeatureDish} onEdit={(x) => setForm({ dish: x })} onToggle={toggle} onDelete={delDish} />)}
          <button type="button" onClick={() => setForm({ dish: null, cat: c.id })} className="text-[11px] font-black uppercase tracking-widest text-[#D4AF37] flex items-center gap-1" data-testid={`add-dish-${c.id}`}><Plus size={12} /> Ajouter un plat</button>
        </div>
      ))}
      {featureDish && <FeatureDialog dish={featureDish} isFeatured={featuredId === featureDish.id} onClose={() => setFeatureDish(null)} onDone={() => { setFeatureDish(null); load(); }} />}
      {form && (
        <DishForm user={user} dish={form.dish} defaultCat={form.cat}
          categories={cats} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />
      )}
    </section>
  );
};
